import { yupResolver } from '@hookform/resolvers/yup'
import { map } from 'lodash'
import { ChangeEvent, useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'

import { PageTitle } from '@/components'
import { AlertRatio } from '@/components/AlertRatio'
import { Button, Checkbox, Input, Modal } from '@/components/ui'
import SelectField from '@/components/select-field'
import {
  Partner,
  PartnerCategory,
  PartnerPayload,
} from '../../../common/models'
import FormGroup from '../../../core/components/FormGroup'
import { FormUploadPreview } from '../../../core/components/FormUploadPreview'
import {
  createPartner,
  deletePartner,
  getPartners,
  updatePartner,
} from '../../../core/services/partners'
import { uploadImage } from '../../../core/services/upload-image'
import { DeleteConfirmationModal } from '../DeleteConfirmationModal'
import { MessageBox } from '../messages/MessageBox'
import { Icon } from '@/common/components/Icon'

interface IPartnerValues {
  name?: string
  photo?: string
  published?: boolean
  publishAt?: number
  priority?: number
  partnerCategory?: string
}

export interface CreatePartnerListModalProps {
  showCreateModal: boolean
  setShowCreateModal: (show: boolean) => void
  setEditMode: (editMode: boolean) => void
  editMode: boolean
  page: number
  size: number
  setTotalElemenets: (elements: number) => void
  setPartners: (partners: Partner[]) => void
  partner?: Partner
  categories: PartnerCategory[]
}

export const CreatePartnerListModal = (
  props: CreatePartnerListModalProps,
): JSX.Element => {
  const {
    showCreateModal,
    setShowCreateModal,
    setEditMode,
    editMode,
    page,
    size,
    setTotalElemenets,
    partner,
    categories,
    setPartners,
  } = props
  const [showDeleteConfirmationModal, setDeleteConfirmationModal] =
    useState<boolean>(false)
  const [image, setImage] = useState('')
  const [imageName, setImageName] = useState('')
  const [isUploading, setIsUploading] = useState<boolean>(false)
  const [showDeleteLogoModal, setShowDeleteLogoModal] = useState<boolean>(false)
  const [invalidPhoto, setInvalidPhoto] = useState<boolean>(false)

  const [isError, setIsError] = useState<boolean>(false)
  const [isErrorRatio, setIsErrorRatio] = useState<boolean>(false)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')

  useEffect(() => {
    if (editMode && partner) {
      setValue('name', partner?.name)
      setValue('priority', partner?.priority)
      setValue('published', partner?.published)
      setValue('partnerCategory', partner?.partnerCategory?.id)
      setValue('photo', partner?.imageLinkLg)
      setImage(partner.imageLinkLg)
      if (partner?.imageLinkLg) {
        const url = new URL(partner.imageLinkLg)
        const i = url?.pathname?.lastIndexOf('/') + 1
        const imageName = url?.pathname?.substring(i)
        setImageName(imageName)
      }
    }

    return () => {
      setImage('')
      setImageName('')
      reset()
    }
  }, [partner, editMode])

  const schema = yup.object().shape({
    name: yup.string(),
    photo: yup.string(),
    published: yup.boolean(),
    publishAt: yup.number(),
    priority: yup.number(),
    partnerCategory: yup.string(),
  })

  const {
    handleSubmit,
    register,
    formState: { errors, dirtyFields },
    reset,
    setValue,
    control,
  } = useForm<IPartnerValues>({
    mode: 'onChange',
    resolver: yupResolver<IPartnerValues>(schema),
    defaultValues: {
      name: '',
      photo: '',
      priority: 0,
      published: false,
      publishAt: new Date().valueOf(),
    },
  })

  const handleDelete = () => {
    setDeleteConfirmationModal(true)
  }

  const deletePartnerFromModal = async () => {
    try {
      if (partner) {
        const { status } = await deletePartner([partner.id])
        const response = await getPartners({
          page: page - 1,
          size,
        })
        setTotalElemenets(response.data.totalElements)
        setPartners(response.data.content)
        if (status === 204 && response.status === 200) {
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Delete Successful')
        }
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    } finally {
      handleClose()
    }
  }

  const handleClose = () => {
    setShowCreateModal(false)
    setImage('')
    setImageName('')
    setDeleteConfirmationModal(false)
    setShowDeleteLogoModal(false)
    setEditMode(false)
    reset()
  }

  const submit = async (values: IPartnerValues) => {
    try {
      const data: PartnerPayload = {
        name: values.name,
        priority: values.priority,
        published: values.published,
        publishAt: values.publishAt,
        partnerCategoryId:
          values.partnerCategory === 'none'
            ? undefined
            : values.partnerCategory,
      }
      if (values.photo) {
        data.imageLinkMd = values.photo
        data.imageLinkLg = values.photo
      }
      if (partner) {
        const { status } = await updatePartner(partner.id, data)
        const response = await getPartners({
          page: page - 1,
          size,
        })
        setPartners(response.data.content)
        if (status === 200 && response.status === 200) {
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Update Successful')
        }
      } else {
        const { status } = await createPartner(data)
        const response = await getPartners({
          page: page - 1,
          size,
        })
        setTotalElemenets(response.data.totalElements)
        setPartners(response.data.content)
        if (status === 200 && response.status === 200) {
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Create Successful')
        }
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    } finally {
      handleClose()
    }
  }

  const handleLogoDelete = () => {
    setImage('')
    setImageName('')
    setValue('photo', '')
  }

  return (
    <>
      <Modal
        open={showCreateModal}
        onOpenChange={(open) => {
          if (!open) handleClose()
        }}
        size="xl"
        title={
          <div className="border-b border-monochrome-120 pb-4">
            <PageTitle
              title="Partners"
              subtitle={editMode ? `Edit ${partner?.name}` : 'Add New Partner'}
            />
          </div>
        }
        footer={
          <div className="flex w-full items-center gap-2">
            {editMode && (
              <Button
                onClick={handleDelete}
                variant="destructive"
                className="mr-auto"
              >
                Delete
                <Icon
                  icon="close-circle"
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                />
              </Button>
            )}
            <Button onClick={handleClose} type="button" variant="secondary">
              Cancel
            </Button>
            <Button
              form="partner-list-form"
              type="submit"
              variant="primary"
              disabled={isUploading || Object.keys(dirtyFields).length === 0}
            >
              Save
            </Button>
          </div>
        }
      >
        <form
          id="partner-list-form"
          className="space-y-7"
          onSubmit={handleSubmit(submit)}
        >
          <Controller
            name="partnerCategory"
            control={control}
            render={({ field }) => (
              <SelectField
                label="Partners Category"
                placeholder="Select Partner Category"
                value={field.value}
                outline
                onValueChange={field.onChange}
                options={map(categories, (category) => ({
                  value: category.id,
                  label: category.name,
                }))}
                error={errors?.partnerCategory?.message}
              />
            )}
          />
          <FormGroup label="Name">
            <Input
              isInvalid={!!errors?.name?.message}
              {...register('name')}
              type="text"
              placeholder="Enter Name"
            />
          </FormGroup>
          <FormUploadPreview
            control={
              <Input
                multiple={false}
                accept="image/*"
                isInvalid={
                  !!errors?.photo?.message || invalidPhoto || isErrorRatio
                }
                name="photo"
                onChange={async (e: ChangeEvent<HTMLInputElement>) => {
                  const file = e?.target?.files?.[0]
                  if (file) {
                    if (file.size > 1_000_000) {
                      setInvalidPhoto(true)
                      setIsError(true)
                      setShowMessageBox(true)
                      setMessage('The photo is too large (1 MB)')
                      return
                    }
                    const photo = await uploadImage(
                      file,
                      setIsUploading,
                      setIsError,
                      setShowMessageBox,
                      setMessage,
                      setIsErrorRatio,
                      'PARTNERS_LOGO',
                    )
                    if (photo?.photoUrl) {
                      setImage(photo.photoUrl)
                      setImageName(file.name)
                      setValue('photo', photo.photoUrl, {
                        shouldValidate: true,
                        shouldDirty: true,
                      })
                    }
                  }
                }}
                type="file"
                className="bg-monochrome-5-50"
              />
            }
            imageName={imageName}
            isUploading={isUploading}
            url={image}
            onRemove={() => {
              setShowDeleteLogoModal(true)
            }}
            imageStyle={{
              objectFit: 'contain',
            }}
            label="Logo (300x300)"
            previewLabel="Logo (300x300)"
          />
          {isErrorRatio && <AlertRatio />}
          <FormGroup label="Priority">
            <Input
              isInvalid={!!errors?.priority?.message}
              {...register('priority')}
              type="number"
              min={0}
              max={100}
              placeholder="Enter Weight"
            />
          </FormGroup>
          <Controller
            name="published"
            control={control}
            render={({ field }) => (
              <FormGroup label="Published" direction="row">
                <Checkbox
                  className="border-monochrome-1"
                  checked={Boolean(field.value)}
                  onCheckedChange={(checked) => {
                    field.onChange(Boolean(checked))
                  }}
                />
              </FormGroup>
            )}
          />
        </form>
      </Modal>
      <DeleteConfirmationModal
        showDeleteConfirmationModal={showDeleteConfirmationModal}
        setDeleteConfirmationModal={setDeleteConfirmationModal}
        deleteTitle={'Partner'}
        deleteFunc={deletePartnerFromModal}
        deletedItem={partner?.name}
      />
      <DeleteConfirmationModal
        showDeleteConfirmationModal={showDeleteLogoModal}
        setDeleteConfirmationModal={setShowDeleteLogoModal}
        deleteTitle={'Logo'}
        deleteFunc={handleLogoDelete}
        deletedItem={imageName}
      />
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
      />
    </>
  )
}
